export const PROGRAM_TYPES = ["recreational", "competitive", "camp", "league"];

export const normalizeProgramType = (programType) =>
  programType === "camps" ? "camp" : programType;

export const getActiveProgramEmailSettings = ({ isLeague, emailSettings, confirmationEmail }) => isLeague
  ? {
      ...emailSettings,
      confirmation_subject: confirmationEmail.subject,
      confirmation_body: confirmationEmail.body,
    }
  : emailSettings;

export const buildProgramEmailSettingsPayload = (settings) => ({
  ...settings,
});

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== "";

export const getProgramReadiness = ({ programDetails, divisions, paymentPlans, emailSettings }) => {
  const issues = [];
  const type = normalizeProgramType(programDetails.program_type);

  if (!PROGRAM_TYPES.includes(type)) {
    issues.push({ step: 1, field: "program_type", label: "Choose a supported program type" });
  }

  [
    ["name", "Add a program name"],
    ["sport", "Choose a sport"],
    ["org_id", "Choose an organization"],
    ["start_date", "Add a start date"],
    ["end_date", "Add an end date"],
    ["registration_deadline", "Add a registration deadline"],
  ].forEach(([field, label]) => {
    if (!hasValue(programDetails[field])) issues.push({ step: 2, field, label });
  });

  const start = programDetails.start_date && new Date(`${programDetails.start_date}T00:00:00`);
  const end = programDetails.end_date && new Date(`${programDetails.end_date}T00:00:00`);
  const deadline = programDetails.registration_deadline && new Date(`${programDetails.registration_deadline}T00:00:00`);
  const opens = programDetails.registration_open_date && new Date(`${programDetails.registration_open_date}T00:00:00`);
  if (start && end && end < start) issues.push({ step: 2, field: "end_date", label: "End date must be on or after the start date" });
  if (deadline && start && deadline > start) issues.push({ step: 2, field: "registration_deadline", label: "Registration must close by the program start date" });
  if (opens && deadline && opens > deadline) issues.push({ step: 2, field: "registration_open_date", label: "Registration must open before it closes" });

  if (!divisions.length) {
    issues.push({ step: 3, label: "Add at least one division" });
  } else {
    divisions.forEach((division, index) => {
      if (!hasValue(division.name)) issues.push({ step: 3, field: `divisions.${index}.name`, label: `Add a name for division ${index + 1}` });
      if (!hasValue(division.birth_date_start)) issues.push({ step: 3, field: `divisions.${index}.birth_date_start`, label: `Add the oldest eligible birth date for division ${index + 1}` });
      if (!hasValue(division.birth_date_end)) issues.push({ step: 3, field: `divisions.${index}.birth_date_end`, label: `Add the youngest eligible birth date for division ${index + 1}` });
      if (!hasValue(division.price)) issues.push({ step: 3, field: `divisions.${index}.price`, label: `Add a price for division ${index + 1}` });
      else if (Number(division.price) < 0) issues.push({ step: 3, field: `divisions.${index}.price`, label: `Division ${index + 1} price cannot be negative` });
      if (Number(division.max_players) <= 0) issues.push({ step: 3, field: `divisions.${index}.max_players`, label: `Division ${index + 1} capacity must be greater than zero` });
    });
  }

  paymentPlans.forEach((plan, index) => {
    if (!hasValue(plan.name) || Number(plan.num_payments) < 1) {
      issues.push({ step: 4, field: !hasValue(plan.name) ? `paymentPlans.${index}.name` : `paymentPlans.${index}.num_payments`, label: `Complete payment plan ${index + 1}` });
    }
    if (Number(plan.deposit || 0) < 0) issues.push({ step: 4, field: `paymentPlans.${index}.deposit`, label: `Payment plan ${index + 1} deposit cannot be negative` });
  });

  if (!hasValue(emailSettings.confirmation_subject)) issues.push({ step: 6, field: "confirmation_subject", label: "Add a confirmation email subject" });
  if (!hasValue(emailSettings.confirmation_body)) issues.push({ step: 6, field: "confirmation_body", label: "Add confirmation email content" });

  return { ready: issues.length === 0, issues };
};

export const getProgramStepIssues = (state, step, { league = false } = {}) => {
  const readiness = getProgramReadiness(state);
  const mappedStep = league
    ? (step === 4 ? 3 : step === 6 ? 4 : step === 7 ? 6 : step)
    : step;
  return readiness.issues.filter((issue) => issue.step === mappedStep);
};
